import React, { useState, useEffect, useRef } from 'react'

import cx from 'classnames'
import get from 'lodash/get';
import size from 'lodash/size';
import uniqBy from 'lodash/uniqBy';
import Typewriter from 'typewriter-effect/dist/core'
import { IKImage } from 'imagekitio-react'
import { Transition } from 'react-transition-group'
import { Button } from 'react-bootstrap'
import ScrollToBottom, { useScrollToBottom } from '../../scrollToBottom'
import { SeekContext } from '../../../core/context'
import { getColorsConfig } from '../../../../../components/AssetViewer/helper/cssHelper'
import FileService from '../../../../../components/AssetViewer/helper/fileHelper'
import Content from '../../../../../components/widgets/core/content'
import { previewContent } from '../../../../../constants/seekPreview'

import { SeekSearchBox } from '../../searchInput'
import { injectAktivFallback } from '../../../../widgets/widget.helper'

import styles from './component.module.scss'


const DefaultAssetIcon = '/images/default_asset.png'

export const SessionContainer = ({
  widgetProps,
  handleSuggestionClick,
  onSubmit,
  breakpoint,
  onClose,
  preview = false
}) => {
  const seekContext = React.useContext(SeekContext)

  const id = get(widgetProps, 'uid')
  const [state, setState] = useState({
    disableTyping: false,
    question: '',
    showChats: false
  })

  const widgetProperties = get(widgetProps, 'properties')

  const seekResponseProps = get(
    widgetProperties,
    `${breakpoint}.responseDesign`,
    {}
  )
  const accentColor = get(seekResponseProps, 'accentColor', {
    r: 245,
    g: 245,
    b: 245,
    a: 1
  })
  const layoutType = get(
    seekResponseProps,
    'type',
    breakpoint === 'desktop' ? 'inline' : 'popup'
  )

  const fontStyles = get(widgetProperties, `${breakpoint}.font`, {})

  const containerStyles = getBackgroundStyles(seekResponseProps);

  const loading = get(seekContext, `seekSession.${id}.loading`, false)
  const session = preview ? previewContent : get(seekContext, `seekSession.${id}.item`, null);
  const chats = get(session, 'chats')

  const handleDisableTyping = (value) => {
    setState((state) => {
      return {
        ...state,
        disableTyping: value
      }
    })
  }

  const handleSubmit = () => {
    onSubmit(state.question)
    setState((state) => {
      return {
        ...state,
        question: ''
      }
    })
  }

  return (
    <div className={styles.sessionContainer}>
      <div style={containerStyles} className={styles.responseChatWindow}>
        <div className={styles.header}>
          <div className={styles.logoContainer}></div>
          {layoutType !== 'inline' && (
            <Button
              variant='info'
              className={styles.closeButton}
              onClick={onClose}
            >
              <i className={cx('cleverstory-close', styles.closeIcon)}></i>
            </Button>
          )}
        </div>
        <div className={styles.bodyWrapper}>
          {loading && size(chats) == 0 && (
            <div id='loaderSeek' className={styles.loader}>
              {
                <style>
                  {`
                #loaderSeek i::before {
                  color: ${getColorsConfig(accentColor)};
                }
              `}
                </style>
              }
              <div className={styles.loadMessage}>
                <i className={cx('cleverstory-seek-fan', styles.loadIcon)}></i>
              </div>
            </div>
          )}
          { size(chats) > 0 && (
            <ScrollToBottom
              mode = {preview ? 'top' : 'bottom'}
              initialScrollBehavior='smooth'
              className={cx(styles.chatContainer, {
                [styles.activeChat]: session
              })}
            >
              <SeekSessionContainer
                widget={id}
                onDisableTyping={handleDisableTyping}
                widgetProps={widgetProps}
                seekResponseProps={seekResponseProps}
                fontStyles={fontStyles}
                session={session}
              />
            </ScrollToBottom>
          )}
          <SeekSearchBox
            widgetProps={widgetProps}
            newChat={true}
            value={state.question}
            onChange={(value) => {
              setState((state) => {
                return {
                  ...state,
                  question: value
                }
              })
            }}
            disabled={state.disableTyping}
            handleSuggestionClick={handleSuggestionClick}
            className={styles.searchInputField}
            breakpoint={breakpoint}
            onSubmit={handleSubmit}
          />
        </div>
      </div>
      <SeekInfoFooter />
    </div>
  )
}

export const SeekSessionContainer = ({
  widget,
  onDisableTyping,
  widgetProps,
  seekResponseProps,
  fontStyles,
  session
}) => {
  const seekContext = React.useContext(SeekContext)
  const loading = get(seekContext, `seekSession.${widget}.loading`, false)
  const scrollToBottom = useScrollToBottom()

  useEffect(() => {
    if (loading) {
      scrollToBottom()
    }
  }, [loading])

  return (
    <div className={styles.session}>
      {(get(session, 'chats') || []).map((chat, index) => {
        return (
          <SeekChatContainer
            widgetProps={widgetProps}
            onDisableTyping={onDisableTyping}
            seekResponseProps={seekResponseProps}
            fontStyles={fontStyles}
            key={index}
            chat={chat}
            widget={widget}
          />
        )
      })}
    </div>
  )
}

export const SeekChatContainer = ({
  chat,
  onDisableTyping,
  widgetProps,
  seekResponseProps,
  fontStyles,
  widget,
  key
}) => {
  const summaryRef = React.useRef()
  const typewriter = React.useRef(null)
  const scrollToBottom = useScrollToBottom()

  const chatId = get(chat, 'id', null)
  const [state, setState] = useState({
    typeWritter: null,
    isHelpful: false,
    showAssets: false,
    type: 'placeholder',
    animate: chat.querying
  })
  const accentColor = get(seekResponseProps, 'accentColor', {
    r: 0,
    g: 189,
    b: 231,
    a: 1
  })
  const responseColor = get(seekResponseProps, 'responseColor', {
    r: 111,
    g: 111,
    b: 111,
    a: 1
  })
  const questionStyle = getQuestionStyle(fontStyles, responseColor)
  const answerStyle = getAnswerStyle(fontStyles, responseColor)

  const processingMessage = get(
    seekResponseProps,
    'processingMessage',
    'Processing your request...'
  )
  const messageColor = colorStyle(
    get(seekResponseProps, 'processingMessageColor') ?? '#212529'
  );  

  useEffect(() => {
    typewriter.current = new Typewriter(summaryRef.current, {
      delay: 0,
      devMode: false
    })

    setState((state) => {
      return {
        ...state,
        isHelpful: get(chat, 'isHelpful', false)
      }
    })

    if (!chat.querying) {
      updateSummary(chat, state.type)
    } else {
      renderPlaceholderMessage()
    }
  }, [])

  useEffect(() => {
    if (!chat.querying) {
      updateSummary(chat, 'answer')
    } else {
      setState((state) => {
        return {
          ...state,
          animate: chat.querying
        }
      })
    }

    setState((state) => {
      return {
        ...state,
        isHelpful: get(chat, 'isHelpful', false)
      }
    })
  }, [chatId, chat.querying])

  useEffect(() => {
    if (!state.animate && !chat.querying) {
      updateSummary(chat, 'answer')
    }
  }, [])

  useEffect(() => {
    if (state.showAssets) {
      setTimeout(() => {
        scrollToBottom();
      }, 550);
    }
  }, [state.showAssets]);

  useEffect(() => {
    if (!summaryRef.current) return;
  
    const observer = new MutationObserver(() => {
      requestAnimationFrame(() => {
        scrollToBottom();
      });
    });
  
    observer.observe(summaryRef.current, {
      childList: true,
      subtree: true,
      characterData: true
    });
  
    return () => observer.disconnect();
  }, []);

  const updateSummary = (chat, type) => {
    var summary = chat.answer
    if (size(chat.assets) > 0) {
      summary = `${summary} \n\nTo better understand the topic, we suggest you read the asset provided below. It will give you more information and help you learn more.`
    }
    if (state.animate) {
      typewriter.current = new Typewriter(summaryRef.current, {
        delay: 0,
        devMode: false
      })

      onDisableTyping(true)

      typewriter.current
        .typeString(summary)
        .callFunction(() => {
          if (type !== 'placeholder') {
            setState((state) => {
              return {
                ...state,
                showAssets: true,
                animate: false
              }
            })
            onDisableTyping(false)
          }
        })
        .start()
    } else {
      if (type !== 'placeholder') {
        setState((state) => {
          return {
            ...state,
            showAssets: true
          }
        })
      }
      summaryRef.current.innerHTML = summary
    }
  }

  const renderPlaceholderMessage = () => {
    const message = processingMessage

    typewriter.current
    .typeString(`<span style="color: ${messageColor}">${message}</span>`)
      .callFunction(() => {
        setState((state) => {
          return {
            ...state,
            typing: false
          }
        })
      })
      .start()
  }

  return (
    <div key={key} id={`chat_${widget}`} className={styles.chat}>
      <div className={styles.question}>
        <div
          style={{ borderColor: colorStyle(accentColor) }}
          className={cx(styles.avatar, styles.contact)}
        >
          {
            <style>
              {`
                #${`chat_${widget}`} i::before {
                  color: ${colorStyle(accentColor)};
                }
            `}
            </style>
          }
          <i className='cleverstory-user1' />
        </div>
        <p style={questionStyle} className={cx(styles.text)}>
          {chat.question}
        </p>
      </div>
      <div className={styles.answer}>
        <img
          src='/images/seek/seekLogo.png'
          className={cx(styles.avatar, styles.logo)}
        />
        <div className={styles.wrapper}>
          <div
            id='summaryElement'
            style={answerStyle}
            className={styles.summary}
            ref={summaryRef}
          ></div>
          <Assets
            chat={chat}
            showAssets={state.showAssets}
            seekResponseProps={seekResponseProps}
            widgetProps={widgetProps}
          />
        </div>
      </div>
    </div>
  )
}

export const SeekInfoFooter = () => {
  return (
    <div className={styles.footerWrapper}>
      <a
        className={styles.knowMore}
        href='https://experiences.cleverstory.io/introducing-seek'
        target='_blank'
      >
        <i className={cx('cleverstory-help', styles.infoIcon)}></i>
      </a>
      <div className={styles.infoTextWrapper}>
        <span className={styles.label}>Seek - Powered by AI</span>
      </div>
    </div>
  )
}

const Assets = ({ chat, showAssets, widgetProps, seekResponseProps }) => {
  const contentMappings = get(chat, 'contentMappings') || [];
  const pages = get(chat, 'pages') || [];
  const responseColor = get(seekResponseProps, 'responseColor', {
    r: 111,
    g: 111,
    b: 111,
    a: 1
  })

  let nameStyles = {}

  const color = getColorStyles(responseColor)
  nameStyles = {
    ...nameStyles,
    ...color
  }
  
  return (
    <Transition
      in={showAssets && (contentMappings.length > 0 || pages.length > 0)}
      timeout={{ enter: 500, exit: 300 }}
    >
      {(state) => (
        <React.Fragment>
          <div className={cx(styles.assets, styles[state])}>
            {(uniqBy(chat.contentMappings, 'uid') || []).map((data) => {
              const entityType = get(data, 'content.entityType')
              if (entityType == 'page') {
                return (
                  <PageAsset
                    key={data.uid}
                    data={data}
                    widgetProps={widgetProps}
                    pageSeek={false}
                    nameStyles={nameStyles}
                  />
                )
              }
              return (
                <Asset key={data.id} data={data} widgetProps={widgetProps} nameStyles={nameStyles}/>
              )
            })}
            {pages.map((data) => {
                return (
                  <PageAsset
                    key={data.id}
                    data={data}
                    widgetProps={widgetProps}
                    pageSeek={true}
                    nameStyles={nameStyles}
                  />
                )
            })
            }
          </div>
          <div className={cx(styles.actions, styles[state])}>
            <Feedback
              chat={chat}
              widget={get(widgetProps, 'uid')}
              seekResponseProps={seekResponseProps}
            />
          </div>
        </React.Fragment>
      )}
    </Transition>
  )
}

const Asset = ({ key, data, widgetProps, nameStyles }) => {
  const asset = get(data, 'content.asset')
  const url = asset.alias
  const name = get(asset, 'name', '')
  var extension = FileService.fileType(get(asset, 'metadata'))
  const size = get(asset, 'metadata.content_length')
  const sizeOfAsset = FileService.formatSize(size)
  const pages = get(asset, 'metadata.pages')
  const contentType = get(asset, 'metadata.content_type')
  const contentContext = React.useContext(Content.Context)

  const icon = asset.icon && asset.icon.thumbnail;
  const iconKey = asset.icon && asset.icon.key;

  var link = false
  var additionalData = ''

  if (pages) {
    additionalData = `${asset.metadata.pages} Pages`
  } else if (asset.metadata.video_duration) {
    additionalData = `${formartVideoDuration(asset.metadata.video_duration)}`
  }

  if (contentType.endsWith('url')) {
    link = true
  }

  const handleAssetClick = () => {
    contentContext.viewContent(widgetProps, data)
  }

  return (
    <div key={key} className={styles.asset}>
      <div
        className={styles.iconWrapper}
        onClick={handleAssetClick}
        target='_blank'
      >
        {icon &&
          <img className={cx(styles.icon, styles.trial)} src={icon} alt='img' />
        }
        {!icon && iconKey &&
          <IKImage
            path={iconKey}
            width={90}
            height={110}
            style={{
              borderRadius: '10px'
            }}
          />
        }
        {!icon && !iconKey &&
          <img className={cx(styles.icon, styles.trial)} src={DefaultAssetIcon} alt='img' />
        }
        {link ? <span className={styles.link}>Link</span> : ''}
        {extension ? <span className={styles.extension}>{extension}</span> : ''}
        <span className={styles.additionalData}>{additionalData}</span>
        {size ? <span className={styles.size}>{sizeOfAsset}</span> : ''}
        <div className={styles.overlay}></div>
      </div>

      <div className={styles.name} style={nameStyles}>{name}</div>
    </div>
  )
}

const PageAsset = ({ key, data, widgetProps, pageSeek, nameStyles }) => {
  const asset = pageSeek ? data : get(data, 'content.asset');
  const icon = asset.icon ? asset.icon.url : DefaultAssetIcon
  const domain = get(asset, 'domain', '');
  const canonical = get(asset, 'canonical', '');
  const name = get(asset, 'name', '')
  const storyboardType = get(asset, 'storyboardType')
  const contentContext = React.useContext(Content.Context)

  const handleAssetClick = () => {
    if (pageSeek){
      window.open(`${domain}${canonical}`);
      return;
    }
    contentContext.viewContent(widgetProps, data)
  }

  const pageTypeLabel = () => {
    switch (storyboardType) {
      case 'INTERACTIVE_CONTENT':
        return 'Interactive Content'
      case 'LANDING_PAGE':
        return 'Landing page'
      default:
        return 'Resource Center'
    }
  }

  return (
    <div key={key} className={styles.asset}>
      <div
        className={styles.iconWrapper}
        onClick={handleAssetClick}
        target='_blank'
      >
        <IKImage
          path={icon}
          width={90}
          height={110}
          style={{
            borderRadius: '10px'
          }}
        />
        {storyboardType ? (
          <span className={styles.pages}>{pageTypeLabel()}</span>
        ) : (
          ''
        )}
        <div className={styles.overlay}></div>
      </div>

      <div className={styles.name} style={nameStyles}>{name}</div>
    </div>
  )
}

const formartVideoDuration = (timeSpent) => {
  let timeSpentValue = `${padNumber(timeSpent, 1)} Seconds`

  if (timeSpent >= 60) {
    var minutes = Math.round(timeSpent / 60)
    timeSpentValue = `${padNumber(minutes, 1)} Minute${minutes == 1 ? '' : 's'}`
  }

  return timeSpentValue
}

const padNumber = (number = 0, pad) => {
  number = Math.ceil(number)

  const length = pad - String(number).length + 1
  let padding = ''
  if (length > 0) {
    padding = Array(pad - String(number).length + 1).join('0')
  }
  return padding + number
}

const Feedback = ({ chat, widget, seekResponseProps }) => {
  const seekContext = React.useContext(SeekContext)
  const [helpfulHovered, setHelpfulHovered] = useState(false)
  const [notHelpfulHovered, setNotHelpfulHovered] = useState(false)
  const [helpful, setHelpful] = useState(chat.isHelpful)

  const accentColor = get(seekResponseProps, 'accentColor', {
    r: 0,
    g: 189,
    b: 231,
    a: 1
  })

  const handleFeedback = (value) => {
    if (helpful !== value) {
      setHelpful(value)
    }

    seekContext.updateSeekHelpful(widget, chat.id, value)
  }

  return (
    <div
      className={cx(styles.feedback, {
        [styles.noAssets]: size(chat.assets) === 0
      })}
    >
      {
        <style>
          {`
            #${`chat_${widget}`} i::before {
                color: ${colorStyle(accentColor)};
              }
          `}
        </style>
      }
      <Button
        onMouseEnter={() => setHelpfulHovered(true)}
        onMouseLeave={() => setHelpfulHovered(false)}
        className={cx(styles.thumb, styles.up)}
        onClick={() => handleFeedback(true)}
      >
        {helpful || helpfulHovered ? (
          <i className='cleverstory-like-active' />
        ) : (
          <i className='cleverstory-like' />
        )}
      </Button>
      <Button
        onMouseEnter={() => setNotHelpfulHovered(true)}
        onMouseLeave={() => setNotHelpfulHovered(false)}
        className={cx(styles.thumb, styles.down)}
        onClick={() => handleFeedback(false)}
      >
        {helpful === false || notHelpfulHovered ? (
          <i className='cleverstory-dislike-active' />
        ) : (
          <i className='cleverstory-dislike' />
        )}
      </Button>
    </div>
  )
}

export function getColorStyles(value) {
  if (!value) {
    return null
  }

  const colorRgb = value

  var color = 'inherit'
  if (colorRgb) {
    color = getColorsConfig(colorRgb)
  }

  var styles = {
    color: color
  }

  return styles
}

const getQuestionStyle = (fontStyles, responseColor) => {
  var styles = {}

  if (!fontStyles) {
    return styles
  }
  //font-color
  const color = getColorStyles(responseColor)
  styles = {
    ...styles,
    ...color
  }
  //font-family
  const fontFamily = get(fontStyles, 'fontFamily')
  if (fontFamily) {
    styles = {
      ...styles,
      fontFamily: injectAktivFallback(fontFamily)
    }
  }

  //font-size
  const fontSizeString = get(fontStyles, 'fontSize', '12')
  var fontSize = 'inherit'
  if (fontSizeString) {
    fontSize = parseInt(fontSizeString) + 'px'
  }

  styles['fontSize'] = fontSize

  return styles
}

const getAnswerStyle = (fontStyles, responseColor) => {
  var styles = {}

  if (!fontStyles) {
    return styles
  }

  //font-color
  const color = getColorStyles(responseColor)
  styles = {
    ...styles,
    ...color
  }

  //font-family
  const fontFamily = get(fontStyles, 'fontFamily')
  if (fontFamily) {
    styles = {
      ...styles,
      fontFamily: injectAktivFallback(fontFamily)
    }
  }

  //font-size
  const fontSizeString = get(fontStyles, 'fontSize', '12')
  var fontSize = 'inherit'
  if (fontSizeString) {
    fontSize = parseInt(fontSizeString) + 'px'
  }

  styles['fontSize'] = fontSize

  return styles
}

const getBackgroundStyles = (properties) => {
  var backgroundColor = get(properties, 'backgroundColor', null)

  return {
    backgroundColor: colorStyle(backgroundColor)
  }
}

const colorStyle = (color) => {
  return `rgba(${get(color, 'r') || 0}, ${get(color, 'g') || 0}, ${
    get(color, 'b') || 0
  }, ${get(color, 'a') || 1})`
}
